import React, { useState, useContext } from 'react';
import { Helmet } from "react-helmet";
import { LanguageContext } from '../contexts/LanguageContext';
import { ThemeContext } from '../contexts/ThemeContext';

const ContactUs = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    services: []
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        services: [...formData.services, value]
      });
    } else {
      setFormData({
        ...formData,
        services: formData.services.filter(service => service !== value)
      });
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, we would send the form data to a server
    console.log(formData);
    setFormSubmitted(true);
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: '',
      services: []
    });
  };
  
  const offices = [
    {
      location: translations['contact.offices.lagos.location'] || 'Abuja, Nigeria',
      address: translations['contact.offices.lagos.address'] || '23 Urban Dwell, FCDA, Abuja Nigeria',
      phone: translations['contact.offices.lagos.phone'] || '+234 81 07574322',
      email: translations['contact.offices.lagos.email'] || 'info@fepsec.com'
    },
    {
      location: translations['contact.offices.calgary.location'] || 'Alberta, Canada',
      address: translations['contact.offices.calgary.address'] || '16 Legacy Green, Alberta',
      phone: translations['contact.offices.calgary.phone'] || '+1 825 415 0517',
      email: translations['contact.offices.calgary.email'] || 'info@fepsec.com'
    },
    {
      location: translations['contact.offices.indiana.location'] || 'Indiana, United States',
      address: translations['contact.offices.indiana.address'] || '6213 Bitternut, Indiana',
      phone: translations['contact.offices.indiana.phone'] || '+1 214 306 5779',
      email: translations['contact.offices.indiana.email'] || 'info@fepsec.com'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{translations['contact.pageTitle'] || 'Contact Us | Platform Engineering | Fepsec'}</title>
      </Helmet>
    <div className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">{translations['contact.heading'] || 'Get in Touch'}</h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {translations['contact.subheading'] || "Whether you're looking to transform your infrastructure, optimize your Kubernetes deployment, or just want to learn more about our services, our team is ready to help."}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {offices.map((office, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                <div className="h-48 bg-blue-900 dark:bg-blue-800 flex items-center justify-center p-6">
                  <div className="text-center">
                    <p className="text-blue-300 text-sm uppercase tracking-wider mb-1">{translations['contact.locationLabel'] || 'Fepsec Location'}</p>
                    <h3 className="text-2xl font-bold text-white">{office.location}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{office.address}</p>
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    <p className="text-gray-600 dark:text-gray-300">{office.phone}</p>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <p className="text-gray-600 dark:text-gray-300">{office.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-12 bg-blue-900 text-white">
                <h2 className="text-3xl font-bold mb-6">Let's Start a Conversation</h2>
                <p className="mb-6">
                  Fill out the form and our team will get back to you within 24 hours. We're excited to hear about your 
                  project and how we can help you achieve your goals.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-primary mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <div>
                      <h3 className="font-semibold text-lg">Expert Consultation</h3>
                      <p className="text-blue-200">Get personalized advice from our Kubernetes and cloud-native experts.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-primary mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <div>
                      <h3 className="font-semibold text-lg">Tailored Solutions</h3>
                      <p className="text-blue-200">We'll help you identify the right approach for your specific needs.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-primary mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <div>
                      <h3 className="font-semibold text-lg">No Obligations</h3>
                      <p className="text-blue-200">Our initial consultation is completely free with no strings attached.</p>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-blue-800">
                  <h3 className="font-semibold text-lg mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a href="https://twitter.com/fepsec" target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary transition">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.162 5.656a8.384 8.384 0 01-2.402.658A4.196 4.196 0 0021.6 4c-.82.488-1.719.83-2.656 1.015a4.182 4.182 0 00-7.126 3.814 11.874 11.874 0 01-8.62-4.37 4.168 4.168 0 00-.566 2.103c0 1.45.738 2.731 1.86 3.481a4.168 4.168 0 01-1.894-.523v.052a4.185 4.185 0 003.355 4.101 4.21 4.21 0 01-1.89.072A4.185 4.185 0 007.97 16.65a8.394 8.394 0 01-6.19 1.732 11.83 11.83 0 006.41 1.88c7.693 0 11.9-6.373 11.9-11.9 0-.18-.005-.362-.013-.54a8.496 8.496 0 002.087-2.165z"/>
                      </svg>
                    </a>
                    <a href="https://github.com/fepsec" target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary transition">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.341-3.369-1.341-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.268 2.75 1.026A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.026 2.747-1.026.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.841-2.337 4.687-4.565 4.934.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.137 20.164 22 16.42 22 12c0-5.523-4.477-10-10-10z"/>
                      </svg>
                    </a>
                    <a href="https://www.linkedin.com/company/fepsec" target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary transition">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden p-6">
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 text-green-500 dark:text-green-300 mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{translations['contact.thankYou'] || 'Thank You!'}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-5">{translations['contact.successMessage'] || 'Your message has been sent successfully. We\'ll get back to you as soon as possible.'}</p>
                  <button 
                    onClick={() => setFormSubmitted(false)}
                    className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-2 rounded-md transition"
                  >
                    {translations['contact.sendAnother'] || 'Send Another Message'}
                  </button>
                </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.name'] || 'Name'} *</label>
                        <input 
                          type="text" 
                          id="name" 
                          name="name" 
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder={translations['contact.form.namePlaceholder'] || 'Your full name'}
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.email'] || 'Email'} *</label>
                        <input 
                          type="email" 
                          id="email" 
                          name="email" 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder={translations['contact.form.emailPlaceholder'] || 'your@email.com'}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.company'] || 'Company'}</label>
                        <input 
                          type="text" 
                          id="company" 
                          name="company" 
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder={translations['contact.form.companyPlaceholder'] || 'Your company name'}
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.phone'] || 'Phone'}</label>
                        <input 
                          type="tel" 
                          id="phone" 
                          name="phone" 
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder={translations['contact.form.phonePlaceholder'] || '+234 (80) 123 456 7890'}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.subject'] || 'Subject'} *</label>
                      <input 
                        type="text" 
                        id="subject" 
                        name="subject" 
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={translations['contact.form.subjectPlaceholder'] || 'What do you want to talk about?'}
                      />
                    </div>
                    
                    <div>
                      <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{translations['contact.form.services'] || 'Interested Services'}</legend>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="platform-engineering" 
                            name="services" 
                            value="Platform Engineering"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="platform-engineering" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['services.platformEngineering.title'] || 'Platform Engineering'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="managed-openshift" 
                            name="services" 
                            value="Managed OpenShift"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="managed-openshift" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['services.managedOpenshift.title'] || 'Managed OpenShift'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="kubernetes-consulting" 
                            name="services" 
                            value="Kubernetes Consulting"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="kubernetes-consulting" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['services.kubernetesConsulting.title'] || 'Kubernetes Consulting'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="enterprise-observability" 
                            name="services" 
                            value="Enterprise Observability"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="enterprise-observability" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['services.enterpriseObservability.title'] || 'Enterprise Observability'}</label>
                        </div>
                        
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="physical-security" 
                            name="services" 
                            value="Physical Security"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="physical-security" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['risk.physical'] || 'Physical Security'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="electronics-security" 
                            name="services" 
                            value="Electronics Security"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="electronics-security" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['risk.electronics'] || 'Electronics Security'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="fire-life-safety" 
                            name="services" 
                            value="Fire Life Safety"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="fire-life-safety" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['risk.fireSafety'] || 'Fire Life Safety (FLS)'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="information-security" 
                            name="services" 
                            value="Information Security"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="information-security" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['risk.information'] || 'Information Security'}</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id="cyber-security" 
                            name="services" 
                            value="Cyber Security"
                            onChange={handleCheckboxChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label htmlFor="cyber-security" className="ml-2 text-sm text-gray-700 dark:text-gray-300">{translations['risk.cyber'] || 'Cyber Security'}</label>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{translations['contact.form.message'] || 'Message'} *</label>
                      <textarea 
                        id="message" 
                        name="message" 
                        rows="4" 
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={translations['contact.form.messagePlaceholder'] || 'Tell us about your project and how we can help...'}
                      ></textarea>
                    </div>
                    
                    <div className="flex justify-end">
                      <button 
                        type="submit"
                        className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-3 rounded-md transition"
                      >
                        {translations['contact.form.submit'] || 'Send Message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactUs;
